import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';

import { Camera } from '@yandex-360/iconostasis/dist/icons/Camera';
import { ConferenceStatus, TelemostSettingsSectionKey as SettingsSectionKey } from '@yandex-360/telemost-sdk';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { isPermissionsChangeAllowedSelector } from '../../../pages/call/features/permissions';
import { getConferenceStatus } from '../../../pages/call/selectors';
import SvgImage from '../../components/svg-image';
import { supportsDeviceChange, supportsVirtualBackground } from '../../helpers/browser-capabilities';
import { i18n } from '../../helpers/i18n';
import { useActions } from '../../hooks';
import { isMessengerIntegration as selectIsMessengerIntegration } from '../config/selectors';
import { refreshDevicesListAction, selectIsVirtualBackgroundEnabled } from '../settings';
import { isMobileUa } from '../uatraits/selectors';
import { getUserSelector } from '../user';
import AccountSection from './components/sections/account';
import CameraSection from './components/sections/camera';
import DevelopersSection from './components/sections/developers';
import HelpSection from './components/sections/help';
import { PermissionsSection } from './components/sections/permissions';
import SoundSection from './components/sections/sound';
import ThemeSection from './components/sections/theme';
import VirtualBackgroundSection from './components/sections/virtual-background';
import { HiddenStatus } from './constants';
import { SettingsDesktop } from './desktop';
import { SettingsMobile } from './mobile';
import {
    changeActiveSectionAction,
    closeSettingsModal,
    selectActiveSectionKey,
    selectIsHiddenSectionVisible,
    selectIsSettingsModalOpened,
} from './slice';
import css from './styles.css';
import type { SettingsProps } from './types';
import { MenuItem } from './types';

export const menuItems: MenuItem[] = [
    {
        title: () => i18n('settings', 'virtual-background'),
        color: '#6CD33C',
        section: VirtualBackgroundSection,
        key: SettingsSectionKey.VirtualBackground,
        icon: <SvgImage imageName="virtualBackground" className={css.icon} fill="white" />,
        isHiddenSection: ({ isVirtualBackgroundShown }) =>
            isVirtualBackgroundShown ? HiddenStatus.no : HiddenStatus.always,
        testId: 'virtual-background',
    },
    {
        title: () => i18n('settings', 'account'),
        color: '#0990F2',
        key: SettingsSectionKey.Account,
        section: AccountSection,
        icon: <SvgImage imageName="user" className={css.icon} fill="white" />,
        isHiddenSection: ({ isMessengerIntegration }) =>
            isMessengerIntegration ? HiddenStatus.always : HiddenStatus.no,
        testId: 'account',
    },
    {
        title: () => i18n('settings', 'sound'),
        color: '#FF5C5C',
        key: SettingsSectionKey.Sound,
        section: SoundSection,
        icon: <SvgImage imageName="sound" className={css.icon} fill="white" />,
        testId: 'sound',
    },
    {
        title: () => i18n('settings', 'camera'),
        color: '#09C8F2',
        key: SettingsSectionKey.Camera,
        section: CameraSection,
        icon: <Camera variant="solid" size="sm" color="white" />,
        testId: 'camera',
    },
    {
        title: () => i18n('settings', 'support'),
        color: '#FFA800',
        key: SettingsSectionKey.Help,
        section: HelpSection,
        icon: <SvgImage imageName="help" className={css.icon} fill="white" />,
        testId: 'support',
    },
    {
        title: () => i18n('settings', 'theme'),
        color: '#3BD39C',
        key: SettingsSectionKey.Theme,
        section: ThemeSection,
        icon: <SvgImage imageName="theme" className={css.icon} fill="white" />,
        isHiddenSection: ({ user, isMessengerIntegration }) =>
            user && !isMessengerIntegration ? HiddenStatus.no : HiddenStatus.always,
        testId: 'theme',
    },
    {
        title: () => i18n('settings', 'permissions-members'),
        contentTitle: (isCalendarSummarizationFeatureEnabled) =>
            isCalendarSummarizationFeatureEnabled
                ? i18n('settings-v2', 'permissions-header')
                : i18n('settings', 'permissions-members'),
        color: '#3BD39C',
        key: SettingsSectionKey.Permissions,
        section: PermissionsSection,
        icon: <SvgImage imageName="permissionsBig" className={css.icon} fill="white" />,
        isHiddenSection: ({ showPermissions }) => (showPermissions ? HiddenStatus.no : HiddenStatus.always),
        testId: 'permissions',
    },
    {
        title: () => 'Develop',
        color: '#000000',
        key: SettingsSectionKey.Developers,
        section: DevelopersSection,
        icon: <SvgImage imageName="admin" className={cn(css.icon, css.iconLarge)} fill="white" />,
        isHiddenSection: () => HiddenStatus.unknown,
        testId: 'developers',
    },
];

export const SettingsModal: FunctionComponent<SettingsProps> = ({ parentRef }) => {
    const actions = useActions({ refreshDevicesListAction, closeSettingsModal, changeActiveSectionAction });

    const handleClose = useCallback(() => {
        actions.closeSettingsModal();
    }, [actions]);

    const user = useSelector(getUserSelector);
    const isMessengerIntegration = useSelector(selectIsMessengerIntegration);
    const isMobile = useSelector(isMobileUa);
    const isVirtualBackgroundEnabled = useSelector(selectIsVirtualBackgroundEnabled);

    const isOpened = useSelector(selectIsSettingsModalOpened);
    const isHiddenSectionVisible = useSelector(selectIsHiddenSectionVisible);
    const activeSectionKey = useSelector(selectActiveSectionKey);

    useEffect(() => {
        if (isOpened && supportsDeviceChange()) {
            actions.refreshDevicesListAction();
        }
    }, [isOpened, actions]);

    const conferenceStatus = useSelector(getConferenceStatus);
    const isPermissionsChangeAllowed = useSelector(isPermissionsChangeAllowedSelector);
    const showPermissions = conferenceStatus === ConferenceStatus.JOINED && isPermissionsChangeAllowed;

    const isVirtualBackgroundShown = isVirtualBackgroundEnabled && supportsVirtualBackground();

    const availableMenuItems = useMemo(() => {
        return menuItems.filter(({ isHiddenSection }) => {
            const isHidden = isHiddenSection?.({
                user: user,
                isMessengerIntegration,
                showPermissions,
                isVirtualBackgroundShown,
            });

            switch (isHidden) {
                case HiddenStatus.unknown:
                    return isHiddenSectionVisible;

                case HiddenStatus.always:
                    return false;

                case HiddenStatus.no:
                case undefined:
                    return true;

                default:
                    return true;
            }
        });
    }, [user, isMessengerIntegration, showPermissions, isHiddenSectionVisible, isVirtualBackgroundShown]);

    const defaultMenuItem = isMobile ? null : availableMenuItems[0];
    const selectedItem = useMemo(() => {
        if (!activeSectionKey) {
            return defaultMenuItem;
        }

        const item = availableMenuItems.find(({ key }) => key === activeSectionKey);

        if (
            !isHiddenSectionVisible &&
            item &&
            item.isHiddenSection?.({
                user,
                isMessengerIntegration,
                showPermissions,
                isVirtualBackgroundShown,
            }) === HiddenStatus.unknown
        ) {
            return;
        }

        return item;
    }, [
        availableMenuItems,
        defaultMenuItem,
        isMessengerIntegration,
        activeSectionKey,
        isHiddenSectionVisible,
        isVirtualBackgroundShown,
        showPermissions,
        user,
    ]);

    const setSelectedItem = useCallback(
        (item: MenuItem | null) => {
            actions.changeActiveSectionAction(item?.key || null);
        },
        [actions]
    );

    useEffect(() => {
        if (!selectedItem) {
            setSelectedItem(defaultMenuItem);
        }
    }, [defaultMenuItem, selectedItem, setSelectedItem]);

    const SettingsComponent = isMobile ? SettingsMobile : SettingsDesktop;

    return (
        <SettingsComponent
            availableMenuItems={availableMenuItems}
            selectedItem={selectedItem || null}
            setSelectedItem={setSelectedItem}
            onClose={handleClose}
            visible={isOpened}
            parentRef={parentRef}
        />
    );
};
